import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  ViewChild,
  Input,
  OnInit,
  ContentChild,
  inject,
  AfterContentInit,
  Output,
  EventEmitter,
  contentChild,
  signal,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import { IconCloseComponent, IconSize } from '@pedix-workspace/angular-ui-icons';
import { ModalHistoryComponent } from '../modal-history/modal-history.component';
import { ButtonComponent, ButtonSize } from '@pedix-workspace/angular-ui-button';
import { IconComponent } from '@pedix-workspace/angular-ui-icons';
import { NgTemplateOutlet } from '@angular/common';
import { ModalZIndexService } from '../modal-z-index.service';

export type ModalDialogType = 'confirm' | 'alert' | 'modal';

@Component({
  selector: 'pxw-modal-dialog',
  templateUrl: './modal-dialog.component.html',
  styleUrls: ['./modal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ButtonComponent, ModalHistoryComponent, IconCloseComponent],
})
export class ModalDialogComponent implements OnInit, AfterContentInit {
  @ContentChild(IconComponent) icon: IconComponent;
  @ContentChild('acceptButton') acceptButton: TemplateRef<{
    onConfirmAction: (param?: unknown) => void;
  }>;
  @ContentChild('confirmButton') confirmButton: TemplateRef<{
    onConfirmAction: (param?: unknown) => void;
  }>;
  @ContentChild('cancelButton') cancelButton: TemplateRef<{
    onCancelAction: (param?: unknown) => void;
  }>;

  @Input() iconSize: IconSize = 'md';
  @Input() headingStyle: 'normal' | 'compact' = 'normal';
  @Input() title: string | undefined;
  @Input() type: ModalDialogType = 'alert';
  @Input() acceptText = '';
  @Input() cancelText = '';
  @Input() confirmText = '';
  @Input() showCloseButton = false;
  @Input() confirmDisabled = false;
  @Input() modalClasses: string;
  @Input() size: 'sm' | 'xs' = 'sm';
  @Input() buttonsSize: ButtonSize = 'md';

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<'close' | 'dismiss'>();
  @Output() hidden = new EventEmitter<void>();

  @ViewChild('modalContentWrapper') private modalContentWrapper!: TemplateRef<HTMLElement>;
  @ContentChild('modalContent') modalContent: TemplateRef<{
    onConfirmAction: (param?: unknown) => void;
  }>;

  readonly modalButtons = contentChild<TemplateRef<{ close: () => {} }>>('modalButtons');

  isOpen = signal(false);
  loading = signal(false);
  modalRef: NgbModalRef | undefined;

  get modalTypeClass() {
    return this.modalButtons() ? 'dialog-type-custom' : `dialog-type-${this.type}`;
  }

  confirmAction: (param?: unknown) => void | unknown;
  confirmResolve?: (returnValue: unknown | PromiseLike<unknown>) => void;

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private modalService = inject(NgbModal);
  private t = inject(TranslocoService);
  private modalZIndexService = inject(ModalZIndexService);

  ngOnInit() {
    if (!this.acceptText) {
      this.acceptText = this.t.translate('components.modalDialogAcceptText');
    }
    if (!this.cancelText) {
      this.cancelText = this.t.translate('components.modalDialogCancelText');
    }
    if (!this.confirmText) {
      this.confirmText = this.t.translate('components.modalDialogConfirmText');
    }
  }

  ngAfterContentInit(): void {
    if (this.icon) {
      this.icon.size = this.iconSize;
      this.icon.color = 'pink';
    }
  }

  open<T>(confirmAction?: (param?: unknown) => Promise<T>): Promise<T> {
    this.modalRef = this.modalService.open(this.modalContentWrapper, {
      windowClass: `animated bounceInUp modal-dialog-component dialog-size-${this.size} ${this.modalTypeClass} ${this.modalClasses}`,
      size: 'lg',
      backdrop: 'static',
    });
    // NOTE: this timeout was added to prevent conflict when poping state with modal-history and router navigation if other dialog is closed while opening this new instance
    setTimeout(() => {
      this.toggleIsOpen(true);
    }, 0);

    // Register modal for z-index management
    this.modalZIndexService.registerModal(this.modalRef);

    if (typeof confirmAction === 'function') {
      this.confirmAction = confirmAction;

      const confirmPromise = new Promise<unknown>(resolve => {
        this.confirmResolve = resolve;
      });

      return this.modalRef.result.then(() => confirmPromise as Promise<T>);
    } else {
      this.confirmResolve = undefined;
    }

    return this.modalRef.result.then(value => {
      return new Promise(resolve => {
        // NOTE: this timeout was added to prevent conflict when poping state with modal-history and router navigation on promise resolve
        setTimeout(() => {
          return resolve(value);
        }, 500);
      });
    });
  }

  // NOTE: bound method
  onConfirmAction = async (param?: unknown) => {
    this.loading.set(true);

    try {
      if (typeof this.confirmAction === 'function') {
        const result = await this.confirmAction(param);

        if (this.confirmResolve) {
          // NOTE: this timeout was added to prevent conflict when poping state with modal-history and router navigation on promise resolve
          setTimeout(() => {
            this.confirmResolve?.(result);
          }, 500);
        }
      }

      this.close();
    } catch (error) {
      console.error(error);
    }

    this.loading.set(false);
  };

  onCancelAction = () => {
    this.dismiss('cancel');
  };

  dismiss(reason: string) {
    if (!this.isOpen()) {
      return;
    }
    this.modalRef?.dismiss(reason);
    this.toggleIsOpen(false);
    this.closed.emit('dismiss');
    this.hidden.emit();
  }

  close = () => {
    if (!this.isOpen()) {
      return;
    }
    this.modalRef?.close();
    this.toggleIsOpen(false);
    this.closed.emit('close');
    this.hidden.emit();
  };

  private toggleIsOpen(isOpen: boolean) {
    this.isOpen.set(isOpen);

    this.isOpenChange.emit(isOpen);
  }
}
